import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * How a draft purchase order is sent to the vendor (GEN-2412): by EDI or by
 * email, with the credit terms and the special instruction lines printed on
 * the PO. Defaults come from the vendor master (VENDMAST) and the buyer who
 * released the PO; the buyer can change them when releasing the PO and until it
 * is sent. EDI is the vendor's setup: it can be switched off for one PO, never
 * on for a vendor that does not take EDI.
 */

/** Special instruction lines are 30 characters, at most 3 (AS/400 RPLHDR). */
export const SPECIAL_INSTRUCTION_WIDTH = 30;
export const MAX_SPECIAL_INSTRUCTIONS = 3;
const EMAIL_FIELD_WIDTH = 60;

export const SpecialInstructionSchema = z.object({
  text: z.string().trim().min(1).max(SPECIAL_INSTRUCTION_WIDTH),
  /** The vendor master's usage code for the line (P or B), kept as given. */
  code: z.string().trim().max(1).nullable(),
});
export type SpecialInstruction = z.infer<typeof SpecialInstructionSchema>;

export const CreditTermsCodeSchema = z.object({
  code: z.string(),
  description: z.string().nullable(),
});

export const DraftPurchaseOrderSubmissionSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
  /** False once the PO was sent to the ERP. */
  editable: z.boolean(),
  /** The vendor takes POs by EDI (vendor master); null when not on file. */
  vendorEdi: z.boolean().nullable(),
  /** The vendor master's PO email, offered when EDI is switched off. */
  vendorEmail: z.string().nullable(),
  sendByEdi: z.boolean().nullable(),
  emailTo: z.string().nullable(),
  emailCc: z.string().nullable(),
  emailSubject: z.string().nullable(),
  creditTermsCode: z.string().nullable(),
  creditTermsDescription: z.string().nullable(),
  specialInstructions: z.array(SpecialInstructionSchema),
  /** The division's credit terms codes, for picking another. */
  creditTermsCodes: z.array(CreditTermsCodeSchema),
});
export type DraftPurchaseOrderSubmission = z.infer<
  typeof DraftPurchaseOrderSubmissionSchema
>;

export const GetDraftSubmissionInputSchema = z.object({
  draftPurchaseOrderId: z.string().uuid(),
});

const EmailFieldSchema = z
  .string()
  .trim()
  .email()
  .max(EMAIL_FIELD_WIDTH)
  .nullable()
  .optional();

/**
 * The sending fields a buyer changed, from the release dialog or a draft's
 * Sending panel. Fields left out keep their value.
 */
export const DraftSubmissionChoiceSchema = z.object({
  sendByEdi: z.boolean().optional(),
  emailTo: EmailFieldSchema,
  emailCc: EmailFieldSchema,
  emailSubject: z.string().trim().max(EMAIL_FIELD_WIDTH).nullable().optional(),
  creditTermsCode: z.string().trim().min(1).max(16).nullable().optional(),
  specialInstructions: z
    .array(SpecialInstructionSchema)
    .max(MAX_SPECIAL_INSTRUCTIONS)
    .optional(),
});
export type DraftSubmissionChoice = z.infer<typeof DraftSubmissionChoiceSchema>;

export const SetDraftSubmissionInputSchema = DraftSubmissionChoiceSchema.extend(
  {
    draftPurchaseOrderId: z.string().uuid(),
  },
);
export type SetDraftSubmissionInput = z.infer<
  typeof SetDraftSubmissionInputSchema
>;

export const GetSubmissionDefaultsInputSchema = z.object({
  supplierId: z.string().uuid(),
  warehouseId: z.string().uuid(),
});

/** How a new PO for this vendor and branch would be sent, before it exists. */
export const SubmissionDefaultsSchema = DraftPurchaseOrderSubmissionSchema.omit(
  { draftPurchaseOrderId: true, editable: true },
);
export type SubmissionDefaults = z.infer<typeof SubmissionDefaultsSchema>;

export const purchaseOrderSubmissionContracts = {
  defaults: oc
    .input(GetSubmissionDefaultsInputSchema)
    .output(SubmissionDefaultsSchema)
    .route({
      method: "POST",
      summary: "Get how a new purchase order would be sent",
      description:
        "Returns the vendor master's sending defaults for a new purchase order from this vendor to this branch (EDI or email, email fields, credit terms, special instructions), with the releasing buyer's email, before the order exists.",
    }),
  get: oc
    .input(GetDraftSubmissionInputSchema)
    .output(DraftPurchaseOrderSubmissionSchema)
    .route({
      method: "POST",
      summary: "Get how a draft purchase order is sent",
      description:
        "Returns whether a draft purchase order goes to the vendor by EDI or email, the email fields, credit terms and the special instruction lines printed on it.",
    }),
  set: oc
    .input(SetDraftSubmissionInputSchema)
    .output(DraftPurchaseOrderSubmissionSchema)
    .route({
      method: "POST",
      summary: "Set how a draft purchase order is sent",
      description:
        "Changes how a draft purchase order is sent before it goes to the ERP. EDI can only be on for a vendor set up for EDI.",
    }),
};
